import React from 'react';
import { UPCOMING_EVENTS_LIST_SIZE } from '../api';
import { useScheduleContext } from '../context/ScheduleContext';
import { useUpcomingEventsQuery } from '../queries';
import type { Event } from '../types';
import { useServerNow } from 'shared/queries/serverTime';

/**
 * Returns the selected event for the given schedule, or the first upcoming event
 * when none is selected. Used by pre-rendered panels so each panel has a stable
 * display event without relying on global selected_event.
 */
export function useSelectedEventForSchedule(
  scheduleId: number | string | null | undefined
): Event | null | undefined {
  const { currentScheduleId, selectedEvent } = useScheduleContext();
  const isCurrentSchedule = scheduleId != null && String(scheduleId) === String(currentScheduleId);
  const fromContext = isCurrentSchedule ? selectedEvent : null;
  const serverNowMs = useServerNow();
  const nowSec = Math.floor(Number(serverNowMs || 0) / 1000);

  const { data: upcomingEventsHttp = [] } = useUpcomingEventsQuery({
    scheduleId: scheduleId ?? null,
    numEvents: UPCOMING_EVENTS_LIST_SIZE,
  });
  const upcomingEvents = Array.isArray(upcomingEventsHttp) ? upcomingEventsHttp : [];
  const firstEvent = Array.isArray(upcomingEvents) ? (upcomingEvents[0] as any) : null;
  const firstFutureEvent = React.useMemo(() => {
    const list: any[] = Array.isArray(upcomingEvents) ? upcomingEvents : [];
    if (!list.length) return null;
    const now = Number.isFinite(nowSec) && nowSec > 0 ? nowSec : Math.floor(Date.now() / 1000);
    let best: any = null;
    let bestTs = Infinity;
    for (const ev of list) {
      const ts = ev?.event_timestamp != null ? Number(ev.event_timestamp) : null;
      if (ts == null || !Number.isFinite(ts)) continue;
      if (ts < now) continue;
      if (ts < bestTs) {
        bestTs = ts;
        best = ev;
      }
    }
    return best;
  }, [upcomingEvents, nowSec]);

  return React.useMemo(() => {
    if (fromContext !== undefined && fromContext !== null) return fromContext;
    return (firstFutureEvent ?? firstEvent) ?? undefined;
  }, [fromContext, firstFutureEvent, firstEvent]);
}
